import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Calendar, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { contactInfo, serviceTypes } from '../data/mock';
import { appointmentService } from '../services/api';
import { useAppointmentSlots } from '../hooks/useApi';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    postalCode: '',
    serviceType: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use API hook for appointment slots
  const { slots: appointmentSlots, loading: slotsLoading } = useAppointmentSlots();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    // Format postal code automatically for Canadian format
    if (name === 'postalCode') {
      // Remove spaces and convert to uppercase
      processedValue = value.replace(/\s/g, '').toUpperCase();
      // Add space after 3rd character if length > 3
      if (processedValue.length > 3) {
        processedValue = `${processedValue.slice(0, 3)} ${processedValue.slice(3, 6)}`;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Clean the data before sending
      const cleanData = { ...formData };
      
      // Remove empty optional fields to avoid validation errors
      if (!cleanData.preferredDate) delete cleanData.preferredDate;
      if (!cleanData.preferredTime) delete cleanData.preferredTime;
      if (!cleanData.message) delete cleanData.message;
      
      // Submit appointment via API
      const result = await appointmentService.createAppointment(cleanData);
      
      setFormStatus({
        type: 'success',
        message: '✅ Votre demande a été envoyée avec succès! Salwa a reçu votre demande et vous contactera dans les 24 heures pour confirmer votre rendez-vous. Merci de votre confiance!'
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        postalCode: '',
        serviceType: '',
        preferredDate: '',
        preferredTime: '',
        message: ''
      });
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: error.message || 'Une erreur s\'est produite. Veuillez réessayer ou nous appeler directement.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableSlots = appointmentSlots.filter(slot => slot.available);

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <Calendar size={16} className="text-blue-500" />
            <span>Prise de rendez-vous</span>
          </div>
          
          <h1 className="heading-hero mb-4">
            Contactez-nous
          </h1>
          
          <p className="body-large text-gray-700 max-w-2xl mx-auto">
            Prêt à prendre soin de votre santé ? Contactez-nous dès aujourd'hui pour 
            planifier votre consultation ou poser vos questions.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Phone */}
            <div className="service-card accent-blue text-center hover-lift">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone size={32} className="text-blue-600" />
              </div>
              <h3 className="heading-3 mb-2">Appelez-nous</h3>
              <p className="body-small text-gray-600 mb-4">
                Pour prendre rendez-vous ou poser vos questions
              </p>
              <a 
                href="tel:819-469-4419" 
                className="btn-primary hover-scale"
              >
                <Phone size={16} className="mr-2" />
                <span className="button-text">{contactInfo.phone}</span>
              </a>
              <p className="caption text-gray-500 mt-2">
                Réponse rapide garantie
              </p>
            </div>

            {/* Email */}
            <div className="service-card accent-green text-center hover-lift">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail size={32} className="text-green-600" />
              </div>
              <h3 className="heading-3 mb-2">Écrivez-nous</h3>
              <p className="body-small text-gray-600 mb-4">
                Pour les questions non urgentes
              </p>
              <a 
                href="mailto:salwa.connexion@gmail.com" 
                className="btn-primary hover-scale"
              >
                <Mail size={16} className="mr-2" />
                <span className="button-text">Envoyer un email</span>
              </a>
              <p className="caption text-gray-500 mt-2">
                Réponse sous 24h
              </p>
            </div>

            {/* Location */}
            <div className="service-card accent-cream text-center hover-lift">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin size={32} className="text-orange-600" />
              </div>
              <h3 className="heading-3 mb-2">Zone de service</h3>
              <p className="body-small text-gray-600 mb-4">
                {contactInfo.serviceArea}
              </p>
              <div className="btn-secondary inline-flex items-center">
                <MapPin size={16} className="mr-2" />
                <span className="button-text">Soins à domicile</span>
              </div>
            </div>
          </div>

          {/* Main Contact Form and Info */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="space-y-6">
              <div>
                <h2 className="heading-1 mb-4">Prendre rendez-vous</h2>
                <p className="body-medium text-gray-600">
                  Remplissez le formulaire ci-dessous et nous vous contacterons rapidement 
                  pour confirmer votre rendez-vous.
                </p>
              </div>

              {/* Form Status */}
              {formStatus.message && (
                <div className={`service-card ${formStatus.type === 'success' ? 'accent-green' : 'accent-pink'}`}>
                  <div className="flex items-start space-x-3">
                    {formStatus.type === 'success' ? (
                      <CheckCircle size={20} className="text-green-600 mt-1" />
                    ) : (
                      <AlertCircle size={20} className="text-red-600 mt-1" />
                    )}
                    <p className="body-small">{formStatus.message}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block body-small font-medium mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Votre prénom"
                    />
                  </div>
                  <div>
                    <label className="block body-small font-medium mb-2">
                      Nom de famille *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Votre nom de famille"
                    />
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block body-small font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="votre@email.com"
                    />
                  </div>
                  <div>
                    <label className="block body-small font-medium mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(450) 123-4567"
                    />
                  </div>
                </div>

                {/* Address Info */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block body-small font-medium mb-2">
                      Adresse complète *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="123 Rue Principale, Longueuil"
                    />
                  </div>
                  <div>
                    <label className="block body-small font-medium mb-2">
                      Code postal *
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      required
                      maxLength={7}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="J4K 1A1"
                      style={{ textTransform: 'uppercase' }}
                    />
                    <p className="caption text-gray-500 mt-1">
                      Nous vérifions la disponibilité dans votre secteur
                    </p>
                  </div>
                </div>

                {/* Service Type */}
                <div>
                  <label className="block body-small font-medium mb-2">
                    Type de service *
                  </label>
                  <select
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sélectionnez un service</option>
                    {serviceTypes.map((service) => (
                      <option key={service.value} value={service.value}>
                        {service.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Appointment Preferences */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block body-small font-medium mb-2">
                      Date préférée
                    </label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block body-small font-medium mb-2">
                      Heure préférée
                    </label>
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez une heure</option>
                      {formData.preferredDate && (() => {
                        const selectedDate = new Date(formData.preferredDate + 'T12:00:00');
                        const dayNumber = selectedDate.getDay();
                        
                        // JavaScript getDay(): 0=Dimanche, 1=Lundi, 2=Mardi, 3=Mercredi, 4=Jeudi, 5=Vendredi, 6=Samedi
                        
                        // Vendredi (5) = 3 options complètes
                        if (dayNumber === 5) {
                          return (
                            <>
                              <option value="morning">Matin (8h00-12h00)</option>
                              <option value="afternoon">Après-midi (12h00-16h00)</option>
                              <option value="evening">Soir (16h00-21h00)</option>
                            </>
                          );
                        }
                        
                        // Samedi (6) = 1 option seulement
                        if (dayNumber === 6) {
                          return (
                            <option value="evening">Soir (16h00-21h00)</option>
                          );
                        }
                        
                        // Autres jours = 1 option seulement
                        return (
                          <option value="evening">Soir (16h00-21h00)</option>
                        );
                      })()}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block body-small font-medium mb-2">
                    Message (optionnel)
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Décrivez brièvement vos besoins ou vos questions..."
                  />
                </div>

                {/* Note sur les urgences */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="body-small text-red-800">
                    <strong>Note importante :</strong> Pour les urgences médicales, appelez immédiatement le 911. 
                    Ce formulaire est destiné aux rendez-vous de soins planifiés uniquement.
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full justify-center hover-scale disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      <span className="button-text">Envoi en cours...</span>
                    </div>
                  ) : (
                    <>
                      <Send size={16} className="mr-2" />
                      <span className="button-text">Envoyer la demande</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information & Hours */}
            <div className="space-y-6">
              <div>
                <h2 className="heading-1 mb-4">Informations pratiques</h2>
              </div>

              {/* Hours */}
              <div className="service-card accent-blue">
                <div className="flex items-start space-x-3 mb-4">
                  <Clock size={24} className="text-blue-600 mt-1" />
                  <div>
                    <h3 className="heading-3">Heures d'ouverture</h3>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="body-small">Lundi - Jeudi + Samedi - Dimanche</span>
                    <span className="body-small font-medium">16h00 - 21h00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="body-small">Vendredi</span>
                    <span className="body-small font-medium">8h00 - 21h00</span>
                  </div>
                </div>
              </div>

              {/* Section créneaux supprimée */}

              {/* Information importante */}
              <div className="service-card accent-pink">
                <div className="flex items-start space-x-3">
                  <AlertCircle size={24} className="text-red-600 mt-1" />
                  <div>
                    <h3 className="heading-3 mb-2 text-red-800">Information importante</h3>
                    <p className="body-small text-gray-700 mb-3">
                      Pour les urgences médicales, appelez immédiatement le 911. 
                      Je propose uniquement des soins planifiés à domicile.
                    </p>
                    <a 
                      href="tel:819-469-4419" 
                      className="btn-primary hover-scale"
                    >
                      <Phone size={16} className="mr-2" />
                      <span className="button-text">Nous joindre: {contactInfo.phone}</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Zone de Service */}
              <div className="service-card accent-grey">
                <h3 className="heading-3 mb-4">Notre zone de service</h3>
                <div className="flex items-start space-x-3 mb-4">
                  <MapPin size={24} className="text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="body-medium font-semibold mb-2">Secteurs desservis :</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>Longueuil</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>Brossard</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>   
                        <span>Saint-Lambert</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>Boucherville</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>St-Hubert</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>St-Bruno</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="body-small text-blue-800">
                    <strong>Service à domicile exclusivement</strong><br/>
                    Nous nous déplaçons dans tout le secteur de Longueuil et les environs. 
                    Des frais de déplacement peuvent s'appliquer selon la distance.
                  </p>
                </div>
                <div className="mt-3">
                  <p className="caption text-gray-600">
                    Vous résidez dans un autre secteur ? Contactez-nous pour vérifier la disponibilité du service.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;